import { AfterContentChecked, Component, EventEmitter, forwardRef, Inject, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FilterService } from '@app/core/services/container/filter.service';
import { DivisionContextService } from '@app/core/services/division/division-context.service';
import {
  HierarchySelectionFilterData,
  UserListFilterData
} from '@app/features/user/pages/user-list/user-list-filter/user-list-filter.component.data';
import { FieldOption } from '@app/shared/models/field.interface';
import { CustomOverlayDescriptor } from '@app/shared/services/overlay/custom-overlay.service';
import { EntitySelectionModel } from '@components/data/data-table-new/entitySelectionModel';
import { StateService } from '@components/data/data-table-new/state/state.service';
import { StringTableStrategy } from '@components/form-controls/inputs/input-stringtable/input-stringtable.component';
import { combineLatest, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  hierarchyFilterEntityType,
  hierarchyFilterType,
  HierarchyType,
  hierarchyTypes,
  nodeTypeTag,
  NodeTypeTag
} from '../../models/hierarchy.constants';
import { GetHierarchyDto, NodeDto } from '../../models/hierarchy.model';
import { HierarchyService } from '../../services/hierarchy.service';
import { HierarchyNodeSelection } from '../hierarchy-node-selection/hierarchy-node-selection.component';

@Component({
  selector: 'vfs-hierarchy-selection-filter',
  templateUrl: './hierarchy-selection-filter.component.html',
  styleUrls: ['./hierarchy-selection-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => HierarchySelectionFilterComponent)
    }
  ]
})
export class HierarchySelectionFilterComponent implements OnInit, AfterContentChecked, ControlValueAccessor {
  selectHie: FormControl = new FormControl('');
  @Input() filterData: UserListFilterData;
  @Input() hierarchyType: HierarchyType;
  @Input() ngModel: string[];
  @Input() closeSidenav: boolean;
  @Input() overlaySubject$: Subject<boolean>;
  @Input() customOverlayDescriptor: CustomOverlayDescriptor;
  @Input() formControl: FormControl;
  hierarchy: GetHierarchyDto;

  @Output() ngModelChange = new EventEmitter<string[]>();
  @Output() selectedChange = new EventEmitter<HierarchyNodeSelection>();
  @Output() clear = new EventEmitter();

  hierarchyOptions: FieldOption[] = [];
  stringTreeData: string[] = [];

  node: NodeDto;

  all$: Observable<any>;

  // ControlValueAccessor
  disabled: boolean;
  value: any;

  hierarchyComparison = (option, value): boolean => option.id === value.id;

  constructor(
    @Inject('StateService') protected stateService: StateService,
    protected divisionContextService: DivisionContextService,
    protected filterService: FilterService,
    protected hieService: HierarchyService
  ) {}

  ngOnInit(): void {
    if (this.filterData.hierarchySelectionFilterData && this.filterData.hierarchySelectionFilterData.length > 0) {
      this.filterData.hieValue = this.filterData.hierarchySelectionFilterData[1];
    } else {
      this.filterData.selection.clear();
      this.filterData.hieValue = '';
      this.filterData.hierarchySelectionFilterData = this.getHierarchyNodeSelectionResponse();
      this.stringTreeData = [];
    }

    this.hieService
      .getAllHierarchies()
      .pipe(
        catchError(error => throwError(error)),
        map(
          hierarchies =>
            (this.hierarchyOptions = hierarchies
              .filter(e => (this.hierarchyType ? e.type === this.hierarchyType : true))
              .map(hie => ({
                label: hie.description,
                value: hie
              })))
        )
      )
      .subscribe();

    if (this.ngModel && this.ngModel.length > 0) {
      this.all$ = combineLatest(
        this.hieService.getHierarchy(this.ngModel[1]),
        this.hieService.getHierarchyNode(this.ngModel[1], this.ngModel[2])
      ).pipe(
        map(res => ({ hierarchy: res[0], node: res[1] })),
        tap((res: HierarchyNodeSelection) => {
          this.hierarchy = res.hierarchy;
          this.node = res.node;
          this.selectHie.setValue(res.hierarchy);
          this.stringTreeData = [res.node.id];
          this.filterData.selection.select(res.node);
        })
      );
    } else {
      this.stringTreeData = [];
      this.all$ = of({ hierarchy: null as GetHierarchyDto, node: null });
    }
  }

  ngAfterContentChecked(): void {
    if (!this.filterData.hieValue) {
      this.selectHie.setValue('');
    }
  }

  public nodeStrategy: StringTableStrategy<NodeDto> = (selection: EntitySelectionModel<NodeDto>) => {
    this.filterData.hierarchySelectionFilterData = this.getHierarchyNodeSelectionResponse(selection.selected[0]);
    this.ngModelChange.emit(this.getHierarchyNodeSelectionResponse(selection.selected[0]));
    if (this.formControl) {
      this.formControl.setValue(this.getHierarchyNodeSelectionResponse(selection.selected[0]));
    }
    return selection.selected[0] ? this.hieService.getNodeDescriptor(selection.selected[0]) : '';
  };

  // TODO: type this method parameter
  selectHieChange(value?: { id: string }) {
    if (value) {
      if (value.id !== this.filterData.hieValue) {
        this.filterData.hieValue = value.id;
        this.clearEvent();
      }
    } else {
      this.filterData.hieValue = '';
      this.clearEvent();
    }
  }

  clearEvent() {
    this.filterData.hierarchySelectionFilterData = this.getHierarchyNodeSelectionResponse();
    this.filterData.selection.clear();
    this.stringTreeData = [];
    if (this.formControl) {
      this.formControl.reset();
    }
    this.clear.emit();
  }

  // ControlValueAccessor functions
  onChange = (rating: number) => {};
  onTouched = () => {};

  writeValue(obj: any): void {
    this.value = obj;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private getHierarchyNodeSelectionResponse(node?: NodeDto): HierarchySelectionFilterData {
    let type = this.getType();
    return node ? [type, node.hierarchyId, node.id, hierarchyFilterType.BELOW, hierarchyFilterEntityType.NODE] : null;
  }

  getType(): NodeTypeTag {
    if (this.hierarchyType === hierarchyTypes.USR) {
      return nodeTypeTag.USER;
    } else if (this.hierarchyType === hierarchyTypes.PRD) {
      return nodeTypeTag.PRODUCT;
    } else if (this.hierarchyType === hierarchyTypes.CUS) {
      return nodeTypeTag.CUSTOMER;
    } else {
      // TODO: this else should not exists, check if removing it breaks anything
      return this.hierarchyType as NodeTypeTag;
    }
  }
}
